import { signal, batch, computed, effect } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { DefaultError } from '@tanstack/query-core';
import type { SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { optional } from 'inversify';
import { createStructuredSelector } from 'reselect';
import { injectable, inject } from 'src/features/ioc';
import { isSubscriptionUser } from 'src/lib/subscription/isSubscriptionUser';
import type { CurrentUserModel } from 'src/models/CurrentUserModel';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import type { SessionService } from 'src/services/SessionService';
import { sessionUserDetails } from 'src/services/SessionService/userDetails';
import type { RootState } from 'src/store';
import { clearMasq } from 'src/store/actions/auth';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

const selectUserWithMasq = createStructuredSelector({
    user: (state: RootState) => state.auth?.user,
    masqUser: (state: RootState) => state.auth?.masqUser,
    isSuperUserModeEnabled: (state: RootState) => Boolean(state.ui.superuserAccessEnabledUntil),
    masqExpires: (state: RootState) => state.auth?.masqAccessToken?.expires,
});

type UserData = ReturnType<typeof selectUserWithMasq>;
type SecurityPackageQuery = QueryObserverModel<
    z.infer<typeof SecurityPackage>,
    DefaultError,
    z.infer<typeof SecurityPackage>
>;

@injectable()
export class CurrentUserModelImpl implements CurrentUserModel {
    readonly #disposableStack = new DisposableStack();
    readonly #securityPackageQuery: SecurityPackageQuery;
    #env: string;
    #userData: UserData;
    #sessionUser: UserDetails | undefined;
    #user = signal<UserDetails | undefined>(undefined);
    #userName = signal('');
    #userEmail = signal<string | undefined>(undefined);
    #userId = signal<string | undefined>(undefined);
    #isMasquerading = signal(false);
    #isSuperuser = signal(false);
    #isSuperUserModeEnabled = signal(false);
    #masqExpires = signal<UserData['masqExpires']>(undefined);
    #isDeveloper = signal(false);
    #entitlementsRevision = signal(0);
    #isSubscriptionUser: ReadonlySignal<boolean>;
    #isSubscriptionUserReady: ReadonlySignal<boolean>;
    #store: ReactBindings['Store'];

    // internal value that only developers can toggle on or off
    // on by default for all users who are not developers
    #superUserAlerting = signal(true);

    // this is the public value that determines if the superuser alerting is enabled
    #isSuperUserAlertingEnabled = computed(() => this.#isSuperUserModeEnabled.value && this.#superUserAlerting.value);

    // this is the public value that determines if the user can toggle superuser alerting
    #canToggleSuperUserAlerting = computed(() => this.#isDeveloper.value && this.#env !== 'prod');

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('ConfigService') configService: ReactBindings['ConfigService'],
        @inject('SessionService') @optional() sessionService?: SessionService,
    ) {
        this.#store = store;
        this.#env = configService.environmentName;
        this.#securityPackageQuery = queryObserverModelFactory(getUserSecurityPackageQuery('me'));
        this.#disposableStack.use(this.#securityPackageQuery);
        this.#isSubscriptionUser = computed(() => {
            const securityPackage = this.#securityPackageQuery.signal.value.data;
            return securityPackage !== undefined && isSubscriptionUser(securityPackage.accessList);
        });
        this.#isSubscriptionUserReady = computed(() => {
            const result = this.#securityPackageQuery.signal.value;
            return result.data !== undefined || result.status === 'error';
        });
        this.#userData = selectUserWithMasq(this.#store.getState());
        this.#sessionUser = sessionService ? sessionUserDetails(sessionService.state.value) : undefined;
        this.#updateSignals(this.#userData);

        this.#disposableStack.defer(
            this.#store.subscribeSelector(selectUserWithMasq).handle((userData) => {
                this.#userData = userData;
                this.#updateSignals(userData);
            }),
        );
        if (sessionService) {
            this.#disposableStack.defer(
                effect(() => {
                    this.#sessionUser = sessionUserDetails(sessionService.state.value);
                    this.#updateSignals(this.#userData);
                }),
            );
        }
    }

    #updateSignals(userData: UserData) {
        const user = this.#sessionUser ?? userData.user;
        const userName =
            userData.masqUser !== undefined
                ? `${userData.masqUser.firstName} ${userData.masqUser.lastName}`
                : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

        batch(() => {
            this.#user.value = userData.masqUser ?? user;
            this.#userName.value = userName;
            this.#userEmail.value = userData.masqUser?.email ?? user?.email;
            this.#userId.value = userData.masqUser?.id ?? user?.id;
            this.#isMasquerading.value = userData.masqUser !== undefined;
            this.#isSuperuser.value = user?.isSuperuser ?? false;
            this.#isSuperUserModeEnabled.value = userData.isSuperUserModeEnabled;
            this.#masqExpires.value = userData.masqExpires;
            this.#isDeveloper.value =
                userData.masqUser !== undefined
                    ? (userData.masqUser.isDeveloper ?? false)
                    : (user?.isDeveloper ?? false);

            // in practice userData only changes with masquerading,
            // at which point it is expected the alerting would be reset for the user
            const currentIsDeveloper =
                userData.masqUser !== undefined
                    ? (userData.masqUser.isDeveloper ?? false)
                    : (user?.isDeveloper ?? false);

            if (this.#env === 'prod') {
                // In production, all users (including developers) have alerting enabled
                this.#superUserAlerting.value = true;
            } else {
                // In non-production environments, developers have alerting disabled by default
                this.#superUserAlerting.value = !currentIsDeveloper;
            }
        });
    }

    get userName(): ReadonlySignal<string> {
        return this.#userName;
    }

    get user(): ReadonlySignal<UserDetails | undefined> {
        return this.#user;
    }

    get userEmail(): ReadonlySignal<string | undefined> {
        return this.#userEmail;
    }

    get userId(): ReadonlySignal<string | undefined> {
        return this.#userId;
    }

    get isMasquerading(): ReadonlySignal<boolean> {
        return this.#isMasquerading;
    }

    get isSuperuser(): ReadonlySignal<boolean> {
        return this.#isSuperuser;
    }

    get isSuperUserModeEnabled(): ReadonlySignal<boolean> {
        return this.#isSuperUserModeEnabled;
    }

    get masqExpires(): ReadonlySignal<UserData['masqExpires']> {
        return this.#masqExpires;
    }

    get isDeveloper(): ReadonlySignal<boolean> {
        return this.#isDeveloper;
    }

    get isSuperUserAlertingEnabled(): ReadonlySignal<boolean> {
        return this.#isSuperUserAlertingEnabled;
    }

    get canToggleSuperUserAlerting(): ReadonlySignal<boolean> {
        return this.#canToggleSuperUserAlerting;
    }

    get entitlementsRevision(): ReadonlySignal<number> {
        return this.#entitlementsRevision;
    }

    get isSubscriptionUser(): ReadonlySignal<boolean> {
        return this.#isSubscriptionUser;
    }

    get isSubscriptionUserReady(): ReadonlySignal<boolean> {
        return this.#isSubscriptionUserReady;
    }

    toggleSuperUserMode(enabled: boolean) {
        this.#store.dispatch({
            type: 'superuser::toggle',
            adminModeEnabled: enabled,
        });
    }

    /**
     * This can only be toggled by the user if they are a developer in non-production environments
     * this is not intended to be used by non-developers
     *
     * In production, developers cannot toggle alerting (it's always enabled)
     * In non-production environments, this is intended for developers to turn the superuser alerting on for
     * debugging purposes should such a need arise,
     * but normally developers won't see or hear the alerting
     */
    toggleSuperUserAlerting(enabled: boolean) {
        if (!this.#canToggleSuperUserAlerting.value) {
            return;
        }
        this.#superUserAlerting.value = enabled;
    }

    notifyEntitlementsChanged(): void {
        this.#entitlementsRevision.value = this.#entitlementsRevision.peek() + 1;
    }

    stopMasq() {
        this.#store.dispatch(clearMasq());
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
